import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Send, Loader2, Check, Phone, MapPin } from 'lucide-react';
import { leadsAPI } from '@/lib/api';
import { track, EVENTS } from '@/lib/telemetry';
import { toast } from 'sonner';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Name, email and message are required');
      return;
    }
    setSending(true);
    try {
      await leadsAPI.create({ ...form, source: 'contact_page' });
      track(EVENTS.LEAD_SUBMITTED, { source: 'contact_page' });
      setSent(true);
      setForm({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="contact-page">
      <div className="max-w-3xl">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Mail className="w-7 h-7 text-primary" /> Contact us
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Questions about CBAM, a custom integration, or enterprise pricing? Drop us a
          note — we reply within one business day.
        </p>
      </div>

      <div className="grid md:grid-cols-[1fr_300px] gap-6 max-w-5xl">
        <Card className="p-5" data-testid="contact-form-card">
          {sent ? (
            <div className="text-center py-12" data-testid="contact-sent">
              <Check className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-sm font-medium">Thanks — we will reach out within 24 hours.</p>
              <Button variant="outline" className="mt-4" onClick={() => setSent(false)} data-testid="contact-send-another">
                Send another message
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Full name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                </div>
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Company</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company" />
              </div>
              <div>
                <Label className="text-xs">Message *</Label>
                <Textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  placeholder="How can we help?"
                />
              </div>
              <Button type="submit" disabled={sending} className="w-full" data-testid="contact-submit">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send message
              </Button>
            </form>
          )}
        </Card>

        <Card className="p-5 space-y-4 text-sm" data-testid="contact-info-card">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-1">
              <Mail className="w-3.5 h-3.5" /> Email
            </div>
            <a href="mailto:hello@veridex-one.com" className="text-primary hover:underline">hello@veridex-one.com</a>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-1">
              <Phone className="w-3.5 h-3.5" /> Sales
            </div>
            <a href="mailto:sales@veridex-one.com" className="text-primary hover:underline">sales@veridex-one.com</a>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-1">
              <MapPin className="w-3.5 h-3.5" /> Office
            </div>
            <p className="text-muted-foreground">Hamburg, Germany 🇩🇪</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
